import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, Instagram, Twitter, Youtube } from "lucide-react";
import { NAV_PRODUCT_ITEMS } from "./SiteNavbar";
import { useLegalModals } from "../context/LegalModalsContext";

const PRODUCT_LINKS = NAV_PRODUCT_ITEMS.map(({ label, to }) => ({ label, to }));

const FOOTER_COLS = [
  { title: "Product", links: PRODUCT_LINKS },
  {
    title: "Resources",
    links: [
      { label: "Reading Guides", to: "/reading-guides" },
      { label: "Book Blog", to: "/book-blog" },
      { label: "Help Center", to: "/help-center" },
      { label: "Community", to: "/circles" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Journal", to: "/journal" },
      { label: "Careers", to: "/careers" },
      { label: "Press Kit", to: "/press-kit" },
    ],
  },
];

const linkClass = "text-sm transition-colors hover:opacity-80";
const linkStyle = { color: "var(--text-soft)" };

const FooterLink = ({ link, onOpenLegal }) => {
  if (link.modal) {
    return (
      <button
        type="button"
        onClick={() => onOpenLegal(link.modal)}
        className={`${linkClass} text-left bg-transparent border-none p-0 cursor-pointer font-inherit`}
        style={linkStyle}
      >
        {link.label}
      </button>
    );
  }

  if (link.to) {
    return (
      <Link to={link.to} className={linkClass} style={linkStyle}>
        {link.label}
      </Link>
    );
  }

  return (
    <a href={link.href ?? "#"} className={linkClass} style={linkStyle}>
      {link.label}
    </a>
  );
};

const SiteFooter = () => {
  const { openLegal } = useLegalModals();

  return (
    <footer
      data-testid="footer"
      className="relative pt-24 pb-10"
      style={{
        background: "var(--bg-alt)",
        borderTop: "1px solid var(--border-soft)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-12 mb-14">
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-2.5 mb-5">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, var(--brand) 0%, var(--accent) 100%)",
                }}
              >
                <BookOpen
                  className="w-4.5 h-4.5"
                  style={{ color: "var(--on-brand)" }}
                  strokeWidth={2.3}
                />
              </div>
              <span
                className="font-serif text-xl font-semibold"
                style={{ color: "var(--text)" }}
              >
                PagePeers
              </span>
            </Link>
            <p
              className="text-sm leading-relaxed max-w-sm font-serif italic"
              style={{ color: "var(--text-soft)" }}
            >
              A cozy corner for books and friends. Track your shelf, join a
              circle, and let your next favorite story find you.
            </p>
            <div className="flex items-center gap-2 mt-6">
              {[Twitter, Instagram, Youtube].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  className="w-10 h-10 rounded-full border flex items-center justify-center transition-all hover:scale-105"
                  style={{
                    borderColor: "var(--border)",
                    color: "var(--text-soft)",
                    background: "var(--surface)",
                  }}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-8">
            {FOOTER_COLS.map((col) => (
              <div key={col.title}>
                <h4
                  className="font-serif text-sm font-semibold mb-5"
                  style={{ color: "var(--text)" }}
                >
                  {col.title}
                </h4>
                <ul className="space-y-3">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <FooterLink link={link} onOpenLegal={openLegal} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div
          className="pt-8 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
          style={{ borderColor: "var(--border-soft)" }}
        >
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            © {new Date().getFullYear()} PagePeers. Made with warmth for readers.
          </p>
          <div
            className="flex items-center gap-6 text-xs"
            style={{ color: "var(--text-muted)" }}
          >
            <button
              type="button"
              onClick={() => openLegal("privacy")}
              className="bg-transparent border-none p-0 cursor-pointer font-inherit hover:opacity-80"
              style={{ color: "var(--text-muted)" }}
            >
              Privacy
            </button>
            <button
              type="button"
              onClick={() => openLegal("terms")}
              className="bg-transparent border-none p-0 cursor-pointer font-inherit hover:opacity-80"
              style={{ color: "var(--text-muted)" }}
            >
              Terms
            </button>
            <button
              type="button"
              onClick={() => openLegal("cookies")}
              className="bg-transparent border-none p-0 cursor-pointer font-inherit hover:opacity-80"
              style={{ color: "var(--text-muted)" }}
            >
              Cookies
            </button>
            <Link to="/help-center" className="hover:opacity-80">
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
